import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentThread,
  AgentStreamEvents,
  AgentEvents,
  CreateUserTodoResponse,
  GetAgentThreadsResponse,
  GetAllAssistantTodosResponse,
  GetDailyStandupTodayResponse,
  DailyStandupReadyPayload,
  AssistantTodoItem,
  SubThreadCreatedEvent,
  SubThreadUpdateEvent,
  UserTodoItem,
} from '@dreamai/types';
import CentralTodoList, { DraggedTaskData } from './sidebar/CentralTodoList';
import YourTasksList, { YourTasksListHandle } from './sidebar/YourTasksList';
import AgentTasksList, { AgentTasksListHandle } from './sidebar/AgentTasksList';
import AddTodoModal from './sidebar/AddTodoModal';
import DelegateTaskModal from './sidebar/DelegateTaskModal';
import DreamieAvatarButton from './DreamieAvatarButton';
import QuickAskModal from './QuickAskModal';
import { useAgent } from '../../contexts/AgentContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { usePusher } from '../../contexts/PusherContext';
import { useOnboardingModals } from '../../providers/OnboardingModalsProvider';
import api from '../../api';
import MobileNavToggle from './MobileNavToggle';
import { useMobileNav } from '../../contexts/MobileNavContext';
import { ReactComponent as PlusAddIcon } from '../../icons/plus-add.svg';
import { ReactComponent as TodoSpinnerIcon } from '../../icons/todo-spinner.svg';
import { ReactComponent as HourglassIcon } from '../../icons/hourglass.svg';
import { ReactComponent as AudioWavesIcon } from '../../icons/audio-waves.svg';
import { ReactComponent as ChatBubbleIcon } from '../../icons/chat-bubble.svg';
import { generateColorVariants } from './sidebar/threadUtils';
import NewThreadModal, {
  type CreationType,
} from './ExpansiveSideNavigation/NewThreadModal';
import './todo-list-page.scss';

const TodoListPage: React.FC = () => {
  const { activeAgent } = useAgent();
  const { aiTimeMinutes, resetsAt } = useCompany();
  const { getAccessTokenSilently, user } = useAuth0();
  const userFirstName =
    (user?.given_name as string | undefined) ||
    (user?.nickname as string | undefined) ||
    (typeof user?.name === 'string' ? user.name.split(' ')[0] : undefined) ||
    'You';
  const pusherContext = usePusher(true) as any;
  const navigate = useNavigate();
  const { isMobile } = useMobileNav();
  const [addTodoModalOpen, setAddTodoModalOpen] = useState(false);
  const [modalAgentName, setModalAgentName] = useState<string | undefined>();
  const [projects, setProjects] = useState<AgentThread[]>([]);
  const [optimisticUserTodo, setOptimisticUserTodo] =
    useState<UserTodoItem | null>(null);
  const [optimisticAgentTempTodo, setOptimisticAgentTempTodo] =
    useState<UserTodoItem | null>(null);
  const [confirmedTodoIds, setConfirmedTodoIds] = useState<{
    optimisticId: string;
    realId: string;
  } | null>(null);
  const [confirmedAgentTodoIds, setConfirmedAgentTodoIds] = useState<{
    optimisticId: string;
    realId: string;
  } | null>(null);
  const [delegateModalOpen, setDelegateModalOpen] = useState(false);
  const [draggedTask, setDraggedTask] = useState<DraggedTaskData | null>(null);
  const [removedUserTodoId, setRemovedUserTodoId] = useState<string | null>(
    null,
  );
  const [quickAskOpen, setQuickAskOpen] = useState(false);
  const {
    hasSeenModal,
    markAsSeen,
    loading: modalsLoading,
  } = useOnboardingModals();
  const { voiceCallingEnabled } = useFeatureFlags();
  const showFirstCallPill =
    !modalsLoading && !hasSeenModal('first_call' as any);
  const [initialTodoText, setInitialTodoText] = useState<string | undefined>();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const startCallRef = useRef<(() => void) | null>(null);
  const [dailyStandupId, setDailyStandupId] = useState<string | null>(null);
  const [dailyStandupTeaser, setDailyStandupTeaser] = useState<string | null>(
    null,
  );
  const [dailyStandupExpiresAt, setDailyStandupExpiresAt] = useState<
    string | null
  >(null);
  const [countdown, setCountdown] = useState('');
  const [drawerMenuOpen, setDrawerMenuOpen] = useState(false);
  const [drawerMenuClosing, setDrawerMenuClosing] = useState(false);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [agentSectionTab, setAgentSectionTab] = useState<'active' | 'archived'>(
    'active',
  );
  const [externalCreateType, setExternalCreateType] =
    useState<CreationType | null>(null);

  // ── Bottom sheet drag state ──
  const SHEET_MIN = 15; // vh
  const SHEET_DEFAULT = 45;
  const SHEET_MAX = 90;
  const sheetRef = useRef<HTMLDivElement>(null);
  const [sheetHeight, setSheetHeight] = useState(SHEET_DEFAULT);
  const isDraggingSheet = useRef(false);
  const dragStartY = useRef(0);
  const dragStartH = useRef(0);

  const onHandlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Ignore if user is interacting with a button or link inside the sheet
      const target = e.target as HTMLElement;
      if (target.closest('button, a, [role="button"]')) return;
      e.preventDefault();
      isDraggingSheet.current = true;
      dragStartY.current = e.clientY;
      dragStartH.current = sheetHeight;
      if (sheetRef.current) sheetRef.current.style.transition = 'none';
      (sheetRef.current ?? target).setPointerCapture(e.pointerId);
    },
    [sheetHeight],
  );

  const onHandlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingSheet.current) return;
    const deltaVh =
      ((dragStartY.current - e.clientY) / window.innerHeight) * 100;
    setSheetHeight(
      Math.min(SHEET_MAX, Math.max(SHEET_MIN, dragStartH.current + deltaVh)),
    );
  }, []);

  const onHandlePointerUp = useCallback(() => {
    if (!isDraggingSheet.current) return;
    isDraggingSheet.current = false;
    if (sheetRef.current) sheetRef.current.style.transition = '';
    setSheetHeight((prev) => (prev < 25 ? SHEET_MIN : prev));
  }, []);

  // ── Pull-to-refresh (mobile only) ──
  const PTR_THRESHOLD = 70; // px to trigger refresh
  const PTR_MAX = 120; // px max pull distance
  const yourTasksRef = useRef<YourTasksListHandle>(null);
  const agentTasksRef = useRef<AgentTasksListHandle>(null);
  const ptrWrapperRef = useRef<HTMLDivElement>(null);
  const ptrPullDistanceRef = useRef(0);
  const [ptrPullDistance, setPtrPullDistance] = useState(0);
  const [ptrRefreshing, setPtrRefreshing] = useState(false);
  const [ptrReady, setPtrReady] = useState(false);
  const ptrRefreshingRef = useRef(false);
  ptrRefreshingRef.current = ptrRefreshing;

  const updatePullDistance = useCallback((value: number) => {
    ptrPullDistanceRef.current = value;
    setPtrPullDistance(value);
    setPtrReady(value >= PTR_THRESHOLD);
  }, []);

  const triggerRefresh = useCallback(async () => {
    setPtrRefreshing(true);
    try {
      await Promise.all([
        yourTasksRef.current?.refresh(),
        agentTasksRef.current?.refresh(),
      ]);
    } finally {
      setTimeout(() => {
        setPtrRefreshing(false);
        updatePullDistance(0);
      }, 350);
    }
  }, [updatePullDistance]);

  // Native touch events on the wrapper (required for iOS PWA — React's
  // synthetic touch listeners are passive by default, so preventDefault
  // doesn't work and the browser consumes the gesture as overscroll).
  useEffect(() => {
    if (!isMobile) return;
    const el = ptrWrapperRef.current;
    if (!el) return;

    let startY: number | null = null;
    let tracking = false;

    const onTouchStart = (e: TouchEvent) => {
      if (ptrRefreshingRef.current) return;
      if (el.scrollTop > 0) return;
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      tracking = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking || startY === null) return;
      if (el.scrollTop > 0) {
        tracking = false;
        startY = null;
        updatePullDistance(0);
        return;
      }
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) {
        updatePullDistance(0);
        return;
      }
      // We're actively pulling down at the top — prevent native overscroll
      // so iOS doesn't eat the gesture or trigger rubber-band scrolling.
      e.preventDefault();
      const resisted = Math.min(PTR_MAX, delta * 0.55);
      updatePullDistance(resisted);
    };

    const onTouchEnd = () => {
      if (!tracking) return;
      tracking = false;
      startY = null;
      if (ptrPullDistanceRef.current >= PTR_THRESHOLD) {
        triggerRefresh();
      } else {
        updatePullDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isMobile, triggerRefresh, updatePullDistance]);

  // ── Daily standup check ──
  useEffect(() => {
    let cancelled = false;
    const checkStandup = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetDailyStandupTodayResponse>(
          '/daily-standup/today',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled && res.data.success && res.data.standup) {
          const s = res.data.standup;
          if (s.status === 'ready') {
            setDailyStandupId(s.id);
            setDailyStandupTeaser(s.teaser || null);
            setDailyStandupExpiresAt(s.expiresAt || null);
            setDailyStandupExpiresAt(s.expiresAt || null);
          }
        }
      } catch {
        // Silently ignore — standup is optional
      }
    };
    checkStandup();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  // Listen for DAILY_STANDUP_READY Pusher event
  useEffect(() => {
    const userChannel = pusherContext?.userChannel;
    if (!userChannel) return;

    const handleStandupReady = (data: DailyStandupReadyPayload) => {
      setDailyStandupId(data.standupId);
      setDailyStandupTeaser(data.teaser || null);
      setDailyStandupExpiresAt(data.expiresAt || null);
      setDailyStandupExpiresAt(data.expiresAt || null);
    };

    userChannel.bind(AgentEvents.DAILY_STANDUP_READY, handleStandupReady);
    return () => {
      userChannel.unbind(AgentEvents.DAILY_STANDUP_READY, handleStandupReady);
    };
  }, [pusherContext?.userChannel]);

  // ── Countdown timer for rundown expiry ──
  useEffect(() => {
    if (!dailyStandupExpiresAt) {
      setCountdown('');
      return;
    }
    const tick = () => {
      const diff = new Date(dailyStandupExpiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setCountdown('0h 0m 0s');
        setDailyStandupId(null);
        setDailyStandupExpiresAt(null);
        return;
      }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setCountdown(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [dailyStandupExpiresAt]);

  // ── Countdown timer for rundown expiry ──
  useEffect(() => {
    if (!dailyStandupExpiresAt) {
      setCountdown('');
      return;
    }
    const tick = () => {
      const diff = new Date(dailyStandupExpiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setCountdown('0h 0m 0s');
        setDailyStandupId(null);
        setDailyStandupExpiresAt(null);
        return;
      }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setCountdown(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [dailyStandupExpiresAt]);

  const [agentTodos, setAgentTodos] = useState<AssistantTodoItem[]>([]);
  const [agentTempTodos, setAgentTempTodos] = useState<UserTodoItem[]>([]);
  const drawerMenuRef = useRef<HTMLDivElement>(null);

  // Fetch agent todos + temp todos for drawer status pill
  useEffect(() => {
    if (!activeAgent?.id) return;
    const fetchAgentTodos = async () => {
      try {
        const token = await getAccessTokenSilently();
        const [todosRes, tempRes] = await Promise.all([
          api.get<GetAllAssistantTodosResponse>(
            `/todos?agentId=${activeAgent.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          api.get<{ success: boolean; tempTodos: any[] }>(
            `/user-todos/temp?agentId=${activeAgent.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);
        setAgentTodos(todosRes.data.todos || []);
        setAgentTempTodos(
          (tempRes.data.tempTodos || []).map((t: any) => ({
            id: t.id,
            description: t.description,
            status: 'pending' as const,
            assigneeType: 'agent' as const,
            threadId: t.threadId,
            agentId: t.agentId,
            createdAt: t.createdAt,
            updatedAt: t.createdAt,
          })),
        );
      } catch (err) {
        console.error('[TodoListPage] Error fetching agent todos:', err);
      }
    };
    fetchAgentTodos();
  }, [activeAgent?.id, getAccessTokenSilently]);

  // Optimistically add temp todo when user creates one for agent
  useEffect(() => {
    if (!optimisticAgentTempTodo) return;
    setAgentTempTodos((prev) => {
      if (prev.some((t) => t.id === optimisticAgentTempTodo.id)) return prev;
      return [optimisticAgentTempTodo, ...prev];
    });
  }, [optimisticAgentTempTodo]);

  // Listen for Pusher sub-thread events to keep drawer status live
  useEffect(() => {
    const channel = pusherContext?.pusherChannel;
    if (!channel) return;

    const handleCreated = (data: SubThreadCreatedEvent) => {
      if (!data.subThread) return;
      if (data.notVisibleInTaskList) return;
      const newTodo: AssistantTodoItem = {
        id: data.subThread.id,
        description: data.subThread.description,
        status: data.subThread.status as any,
        summary: data.subThread.summary,
        createdAt: data.subThread.createdAt,
        updatedAt: data.subThread.updatedAt,
        parentThreadId: data.parentThreadId,
        parentThreadDescription: data.parentThreadDescription || 'Thread',
        parentThreadIsMainThread: data.parentThreadIsMainThread,
        parentThreadIsInternalMonologue: data.parentThreadIsInternalMonologue,
        latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
      };
      setAgentTodos((prev) => {
        if (prev.some((t) => t.id === newTodo.id)) return prev;
        return [newTodo, ...prev];
      });
      // Remove the temp todo this sub-thread replaces
      if (data.replacesTempTodoId) {
        setAgentTempTodos((prev) =>
          prev.filter((t) => t.id !== data.replacesTempTodoId),
        );
      }
      // Clear the optimistic temp todo so the pendingTodo prop becomes null
      // and the AgentTasksList useEffect stops re-adding the pending card.
      setOptimisticAgentTempTodo(null);
      // Remove the temp todo this sub-thread replaces
      if (data.replacesTempTodoId) {
        setAgentTempTodos((prev) =>
          prev.filter((t) => t.id !== data.replacesTempTodoId),
        );
      }
      // Clear the optimistic temp todo so the pendingTodo prop becomes null
      // and the AgentTasksList useEffect stops re-adding the pending card.
      setOptimisticAgentTempTodo(null);
    };

    const handleUpdated = (data: SubThreadUpdateEvent) => {
      if (!data.subThread) return;
      if (data.notVisibleInTaskList) return;
      setAgentTodos((prev) => {
        const idx = prev.findIndex((t) => t.id === data.subThread.id);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          ...data.subThread,
        } as AssistantTodoItem;
        return updated;
      });
    };

    channel.bind(AgentStreamEvents.SUB_THREAD_CREATED, handleCreated);
    channel.bind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdated);
    return () => {
      channel.unbind(AgentStreamEvents.SUB_THREAD_CREATED, handleCreated);
      channel.unbind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdated);
    };
  }, [pusherContext?.pusherChannel]);

  // Derive drawer status from agent todos (same logic as AgentStatusPill)
  const topTodo = useMemo(() => {
    const processing = agentTodos.find((t) => t.status === 'processing');
    if (processing) return processing;
    const waiting = agentTodos.find(
      (t) => t.status === 'waiting_on_third_party',
    );
    if (waiting) return waiting;
    return null;
  }, [agentTodos]);

  const readingCount = agentTempTodos.length;

  const activeCount = useMemo(
    () =>
      agentTodos.filter(
        (t) => t.status !== 'complete' && t.status !== 'waiting_on_user',
      ).length + readingCount,
    [agentTodos, readingCount],
  );

  // Drawer colour variants derived from agent's predominant_colour
  const drawerColorVars = useMemo(() => {
    const colour = activeAgent?.predominant_colour;
    if (!colour) return undefined;
    const cv = generateColorVariants(colour);
    return {
      '--drawer-bg': cv.lighter,
      '--drawer-border': cv.base,
      '--drawer-text': cv.darker,
      '--drawer-count-bg': cv.base,
      '--drawer-btn-bg': cv.midDarker,
    } as React.CSSProperties;
  }, [activeAgent?.predominant_colour]);

  const handleNavigateToThread = useCallback(
    (threadId: string, _parentThreadId?: string, researchAppId?: string) => {
      const params = new URLSearchParams({ thread_id: threadId });
      if (researchAppId) params.set('research_app_id', researchAppId);
      navigate(`/conversations?${params.toString()}`);
    },
    [navigate],
  );

  const agentNickname =
    activeAgent?.nickname || activeAgent?.name || 'Assistant';

  // Credits only gate PA — SA/System are billed separately
  const isPA = (activeAgent?.agent_type as string) === 'Personal Assistant';
  const isOutOfCredits = isPA && aiTimeMinutes <= 0;

  // Fetch threads as projects
  useEffect(() => {
    const fetchProjects = async () => {
      if (!activeAgent?.id) return;
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetAgentThreadsResponse>(
          '/agents/threads',
          {
            params: {
              agent_id: activeAgent.id,
              excludePinned: 'true',
              excludeShared: 'true',
              excludeSubThreads: 'true',
              excludeArchived: 'true',
            },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.data.success) {
          setProjects(
            response.data.threads.filter(
              (t) =>
                !t.is_main_thread &&
                !t.is_archived_thread &&
                !t.is_background &&
                !t.discussing_objects?.some(
                  (obj) =>
                    obj.discussing_object === 'Autopilot' ||
                    obj.discussing_object === 'RehomedTask' ||
                    obj.discussing_object === 'ResponseArtifact' ||
                    obj.discussing_object === 'QuickChat' ||
                    obj.discussing_object === 'TaskParent',
                ),
            ),
          );
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
      }
    };
    fetchProjects();
  }, [activeAgent?.id, getAccessTokenSilently]);

  const handleTaskDropped = useCallback((data: DraggedTaskData) => {
    setDraggedTask(data);
    setDelegateModalOpen(true);
  }, []);

  const handleDelegateSubmit = useCallback(
    async (additionalInfo: string) => {
      if (!draggedTask) return;
      setDelegateModalOpen(false);

      // Build optimistic temp todo
      const optimisticTodo: UserTodoItem = {
        id: `optimistic-${Date.now()}`,
        description: draggedTask.description,
        status: 'pending',
        assigneeType: 'agent',
        agentId: activeAgent?.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically add to agent list
      setOptimisticAgentTempTodo(optimisticTodo);

      // Remove from My Tasks if it was a user todo
      if (draggedTask.type === 'userTodo') {
        setRemovedUserTodoId(draggedTask.id);
      }

      // Fire API in background
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<CreateUserTodoResponse>(
          '/user-todos',
          {
            description: draggedTask.description,
            assigneeType: 'agent',
            agentId: activeAgent?.id,
            additionalContext: additionalInfo || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.tempTodo?.id) {
          setConfirmedAgentTodoIds({
            optimisticId: optimisticTodo.id,
            realId: response.data.tempTodo.id,
          });
        }

        // Mark original user todo as complete if it was a user todo
        if (draggedTask.type === 'userTodo') {
          await api.patch(
            `/user-todos/${draggedTask.id}`,
            { status: 'complete' },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
      } catch (err) {
        console.error('Error delegating task:', err);
      }

      setDraggedTask(null);
    },
    [draggedTask, activeAgent?.id, getAccessTokenSilently],
  );

  const handleDelegateClose = useCallback(() => {
    setDelegateModalOpen(false);
    setDraggedTask(null);
  }, []);

  const openMyTasksModal = useCallback(() => {
    setModalAgentName(undefined);
    setAddTodoModalOpen(true);
  }, []);

  const openAgentTasksModal = useCallback(() => {
    setModalAgentName(agentNickname);
    setAddTodoModalOpen(true);
  }, [agentNickname]);

  const closeModal = useCallback(() => {
    setAddTodoModalOpen(false);
    setInitialTodoText(undefined);
  }, []);

  const openQuickAsk = useCallback(() => {
    setQuickAskOpen(true);
  }, []);

  const closeQuickAsk = useCallback(() => {
    setQuickAskOpen(false);
  }, []);

  const toggleDrawerMenu = useCallback(() => {
    if (drawerMenuOpen && !drawerMenuClosing) {
      setDrawerMenuClosing(true);
      setTimeout(() => {
        setDrawerMenuOpen(false);
        setDrawerMenuClosing(false);
      }, 200);
    } else if (!drawerMenuOpen) {
      setDrawerMenuOpen(true);
    }
  }, [drawerMenuOpen, drawerMenuClosing]);

  const closeDrawerMenu = useCallback(() => {
    if (!drawerMenuOpen) return;
    setDrawerMenuClosing(true);
    setTimeout(() => {
      setDrawerMenuOpen(false);
      setDrawerMenuClosing(false);
    }, 200);
  }, [drawerMenuOpen]);

  // Close drawer menu when clicking outside
  useEffect(() => {
    if (!drawerMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        drawerMenuRef.current &&
        !drawerMenuRef.current.contains(e.target as Node)
      ) {
        closeDrawerMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [drawerMenuOpen, closeDrawerMenu]);

  const handleQuickAskCreateTodo = useCallback(
    (title: string, description: string) => {
      setInitialTodoText(title);
      setModalAgentName(agentNickname);
      setAddTodoModalOpen(true);
    },
    [agentNickname],
  );

  const handleQuickAskCreateProject = useCallback(
    async (
      title: string,
      description: string,
      projectBriefing?: string | null,
    ) => {
      // If the specialist did not produce a briefing (shouldn't happen for
      // create_project_todo, but defend anyway), fall back to the AddTodo
      // modal so we don't silently drop the user's intent.
      if (!projectBriefing || !projectBriefing.trim()) {
        console.warn(
          '[TodoListPage] Project escalation arrived without a briefing; falling back to todo modal',
        );
        setInitialTodoText(title);
        setModalAgentName(agentNickname);
        setAddTodoModalOpen(true);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<{
          success: boolean;
          threadId: string;
          projectName: string;
        }>(
          '/agents/start-project-from-quick-ask',
          {
            projectName: title,
            projectBriefing,
            ...(activeAgent?.id ? { agent_id: activeAgent.id } : {}),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data?.success && response.data.threadId) {
          navigate(`/conversations?thread_id=${response.data.threadId}`);
        } else {
          console.error(
            '[TodoListPage] start-project-from-quick-ask returned no threadId',
            response.data,
          );
        }
      } catch (err) {
        console.error(
          '[TodoListPage] Failed to start project from quick-ask:',
          err,
        );
      }
    },
    [activeAgent?.id, agentNickname, getAccessTokenSilently, navigate],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      const isTyping =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (e.target as HTMLElement)?.isContentEditable ||
        (e.target as HTMLElement)?.getAttribute('role') === 'textbox';

      if (e.key === 'Escape') {
        setAddTodoModalOpen(false);
        return;
      }

      // Ignore when modifier keys are held (Cmd+C, Ctrl+C, etc.)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (isTyping || addTodoModalOpen || quickAskOpen) return;

      if (e.key === 'a') {
        e.preventDefault();
        openQuickAsk();
      } else if (e.key === ' ') {
        e.preventDefault();
        openAgentTasksModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [addTodoModalOpen, quickAskOpen, openQuickAsk, openAgentTasksModal]);

  const formattedDate = new Date()
    .toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    .toUpperCase()
    .replace(',', '');

  return (
    <div className='todo-list-page inset-white-backed'>
      {isMobile ? (
        <div className='mobile-tasks-header'>
          <MobileNavToggle />
          <h1 className='mobile-tasks-title'>Inbox</h1>
        </div>
      ) : (
        <MobileNavToggle />
      )}

      <div
        className={`main-todo-list-area${addTodoModalOpen || delegateModalOpen || quickAskOpen ? ' dimmed' : ''}`}
        onClick={
          isMobile && sheetHeight > SHEET_MIN
            ? () => setSheetHeight(SHEET_MIN)
            : undefined
        }
      >
        {isMobile ? (
          <div
            ref={ptrWrapperRef}
            className={`ptr-wrapper${ptrRefreshing ? ' ptr-refreshing' : ''}${ptrPullDistance > 0 ? ' ptr-pulling' : ''}${ptrReady ? ' ptr-ready' : ''}`}
          >
            {(ptrPullDistance > 0 || ptrRefreshing) && (
              <div
                className='ptr-indicator'
                style={{
                  height: ptrRefreshing
                    ? `${PTR_THRESHOLD}px`
                    : `${ptrPullDistance}px`,
                }}
              >
                <span
                  className={`ptr-spinner${ptrRefreshing ? ' spinning' : ''}`}
                  style={{
                    opacity: ptrRefreshing
                      ? 1
                      : Math.min(1, ptrPullDistance / PTR_THRESHOLD),
                    transform: ptrRefreshing
                      ? undefined
                      : `rotate(${(ptrPullDistance / PTR_THRESHOLD) * 360}deg)`,
                  }}
                >
                  <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M21 12a9 9 0 1 1-3.5-7.13'
                      stroke='currentColor'
                      strokeWidth='2.4'
                      strokeLinecap='round'
                      fill='none'
                    />
                  </svg>
                </span>
                <span className='ptr-label'>
                  {ptrRefreshing
                    ? 'Reloading'
                    : ptrReady
                      ? 'Release to reload'
                      : 'Pull to reload'}
                </span>
              </div>
            )}
            <div
              className='ptr-content'
              style={
                ptrPullDistance > 0 || ptrRefreshing
                  ? {
                      transform: `translateY(${ptrRefreshing ? PTR_THRESHOLD : ptrPullDistance}px)`,
                      transition: ptrRefreshing
                        ? 'transform 0.25s ease'
                        : 'none',
                    }
                  : { transition: 'transform 0.25s ease' }
              }
            >
              <YourTasksList
                ref={yourTasksRef}
                onNavigateToThread={handleNavigateToThread}
                onAddTodo={openMyTasksModal}
                optimisticUserTodo={optimisticUserTodo}
                confirmedTodoIds={confirmedTodoIds}
                removedUserTodoId={removedUserTodoId}
                agentId={activeAgent?.id}
                onOverlayChange={setOverlayOpen}
                onTodoCreated={(todo) => {
                  if (todo.assigneeType === 'user') {
                    setOptimisticUserTodo(todo);
                  } else {
                    setOptimisticAgentTempTodo(todo);
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <>
            <YourTasksList
              ref={yourTasksRef}
              onNavigateToThread={handleNavigateToThread}
              onAddTodo={openMyTasksModal}
              optimisticUserTodo={optimisticUserTodo}
              confirmedTodoIds={confirmedTodoIds}
              removedUserTodoId={removedUserTodoId}
              agentId={activeAgent?.id}
              onOverlayChange={setOverlayOpen}
              onTodoCreated={(todo) => {
                if (todo.assigneeType === 'user') {
                  setOptimisticUserTodo(todo);
                } else {
                  setOptimisticAgentTempTodo(todo);
                }
              }}
            />
            <AgentTasksList
              agentNickname={agentNickname}
              onNavigateToThread={handleNavigateToThread}
              onAddTodo={openAgentTasksModal}
              onCall={
                voiceCallingEnabled ? () => startCallRef.current?.() : undefined
              }
              pendingTodo={
                optimisticAgentTempTodo
                  ? {
                      id: optimisticAgentTempTodo.id,
                      description: optimisticAgentTempTodo.description,
                      tempTodoId:
                        confirmedAgentTodoIds?.optimisticId ===
                        optimisticAgentTempTodo.id
                          ? confirmedAgentTodoIds.realId
                          : undefined,
                    }
                  : null
              }
              agentId={activeAgent?.id}
              agentImageUrl={activeAgent?.image_url}
              agentType={activeAgent?.agent_type}
              onOverlayChange={setOverlayOpen}
              onSectionTabChange={setAgentSectionTab}
              hideFab={true}
            />
          </>
        )}
      </div>

      <DelegateTaskModal
        isOpen={delegateModalOpen}
        onClose={handleDelegateClose}
        onSubmit={handleDelegateSubmit}
        taskDescription={draggedTask?.description || ''}
        agentNickname={agentNickname}
      />

      {/* AddTodoModal is shared between My inbox and Agent's Tasks */}
      <AddTodoModal
        isOpen={addTodoModalOpen}
        onClose={closeModal}
        agentName={modalAgentName}
        agentNickname={agentNickname}
        userFirstName={userFirstName}
        agentImageUrl={activeAgent?.image_url}
        projects={projects}
        isUserTask={!modalAgentName}
        agentId={activeAgent?.id}
        initialText={initialTodoText}
        isMobile={isMobile}
        predominantColour={activeAgent?.predominant_colour}
        onTodoCreated={(todo) => {
          if (todo.assigneeType === 'user') {
            setOptimisticUserTodo(todo);
          } else {
            setOptimisticAgentTempTodo(todo);
          }
        }}
        onTodoConfirmed={(optimisticId, realId) => {
          // Route to the correct confirmed state based on prefix
          if (optimisticId.startsWith('optimistic-')) {
            // Check if we have a matching agent temp todo
            if (optimisticAgentTempTodo?.id === optimisticId) {
              setConfirmedAgentTodoIds({ optimisticId, realId });
            } else {
              setConfirmedTodoIds({ optimisticId, realId });
            }
          }
        }}
      />
      {isMobile && activeAgent?.id && (
        <div
          className={`mobile-agent-sheet${overlayOpen ? ' overlay-active' : ''}`}
          ref={sheetRef}
          style={{ height: `${sheetHeight}vh` }}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
        >
          <div className='sheet-handle' />
          <div className='sheet-content'>
            <AgentTasksList
              ref={agentTasksRef}
              agentNickname={agentNickname}
              onNavigateToThread={handleNavigateToThread}
              onAddTodo={openAgentTasksModal}
              onCall={
                voiceCallingEnabled ? () => startCallRef.current?.() : undefined
              }
              onAsk={openQuickAsk}
              agentImageUrl={activeAgent.image_url}
              pendingTodo={
                optimisticAgentTempTodo
                  ? {
                      id: optimisticAgentTempTodo.id,
                      description: optimisticAgentTempTodo.description,
                      tempTodoId:
                        confirmedAgentTodoIds?.optimisticId ===
                        optimisticAgentTempTodo.id
                          ? confirmedAgentTodoIds.realId
                          : undefined,
                    }
                  : null
              }
              agentId={activeAgent.id}
              agentType={activeAgent.agent_type}
              onOverlayChange={setOverlayOpen}
              onSectionTabChange={setAgentSectionTab}
              dailyStandupId={dailyStandupId}
              countdown={countdown}
              onRundown={
                voiceCallingEnabled ? () => startCallRef.current?.() : undefined
              }
              hideFab={true}
            />
          </div>
        </div>
      )}
      {/* {activeAgent?.id && !overlayOpen && !isMobile && (
        <div className='top-right-launcher'>
          <div className='top-right-launcher-pill'>
            <div className='launcher-avatar'>
              {activeAgent.image_url && (
                <img src={activeAgent.image_url} alt={agentNickname} />
              )}
            </div>
            <button
              className='launcher-icon-btn'
              onClick={() => startCallRef.current?.()}
              aria-label={`Call ${agentNickname}`}
            >
              <AudioWavesIcon />
            </button>
            <button
              className='launcher-icon-btn'
              onClick={openQuickAsk}
              aria-label={`Chat with ${agentNickname}`}
            >
              <ChatBubbleIcon />
            </button>
            <button
              className='launcher-plus-btn'
              onClick={() => openAgentTasksModal()}
              aria-label='Add task'
            >
              <PlusAddIcon />
            </button>
          </div>
          {(showFirstCallPill || dailyStandupId) && (
            <div
              className={`cta-pill${showFirstCallPill && !dailyStandupId ? ' first-call' : ''}`}
            >
              {showFirstCallPill && !dailyStandupId ? (
                <>
                  <div className='cta-pill-text'>
                    <span className='cta-pill-heading'>
                      Ready to get started?
                    </span>
                    <span className='cta-pill-sub'>
                      Jump on a quick call &mdash; I&rsquo;ll walk you through
                      how I work and we can get going right away.
                    </span>
                  </div>
                  <button
                    className='cta-pill-btn'
                    onClick={() => startCallRef.current?.()}
                  >
                    <AudioWavesIcon />
                    Call me
                  </button>
                </>
              ) : (
                <>
                  <div className='cta-pill-text'>
                    <span className='cta-pill-label'>
                      {dailyStandupId ? 'Rundown Ready' : 'Call me'}
                    </span>
                    {dailyStandupId && countdown && (
                      <span className='cta-pill-timer'>{countdown}</span>
                    )}
                  </div>
                  <button
                    className='cta-pill-btn'
                    onClick={() => startCallRef.current?.()}
                  >
                    {dailyStandupId ? "Let's do it!" : 'Make the call'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )} */}
      {/* FAB hidden on mobile — nav provides access to all actions */}
      {activeAgent?.id && !overlayOpen && !isMobile && (
        <DreamieAvatarButton
          agentId={activeAgent.id}
          agentImageUrl={activeAgent.image_url}
          agentNickname={agentNickname}
          isFirstCall={showFirstCallPill}
          dailyStandupId={dailyStandupId || undefined}
          dailyStandupTeaser={dailyStandupTeaser || undefined}
          onFirstCallStarted={() => markAsSeen('first_call' as any)}
          onDailyStandupStarted={() => {
            setDailyStandupId(null);
            setDailyStandupTeaser(null);
            setDailyStandupExpiresAt(null);
            setDailyStandupExpiresAt(null);
          }}
          onTodoCreated={(todo) => setOptimisticAgentTempTodo(todo)}
          onTodoConfirmed={(optimisticId, realId) =>
            setConfirmedAgentTodoIds({ optimisticId, realId })
          }
          onRegisterStartCall={(fn) => {
            startCallRef.current = fn;
          }}
          hideIdleButton={true}
          hideIdleRainbowPills={true}
          mobileSheetHeight={isMobile ? sheetHeight : undefined}
          options={[
            ...(!showFirstCallPill
              ? [
                  {
                    id: 'call',
                    label: 'Call',
                    builtInAction: 'call' as const,
                    shortcut: 'c',
                  },
                ]
              : []),
            {
              id: 'chat',
              label: 'Chat',
              onSelect: openQuickAsk,
              shortcut: 'a',
            },
            {
              id: 'add-todo',
              label: 'Task',
              onSelect: openAgentTasksModal,
              shortcut: ' ',
            },
          ]}
        />
      )}
      <QuickAskModal
        isOpen={quickAskOpen}
        onClose={closeQuickAsk}
        agentId={activeAgent?.id}
        agentImageUrl={activeAgent?.image_url}
        agentNickname={agentNickname}
        predominantColour={activeAgent?.predominant_colour}
        onCreateTodo={handleQuickAskCreateTodo}
        onCreateProject={handleQuickAskCreateProject}
      />
      {activeAgent?.id && (
        <NewThreadModal
          isOpen={externalCreateType !== null}
          onClose={() => setExternalCreateType(null)}
          agentId={activeAgent.id}
          agentName={agentNickname}
          agentImageUrl={activeAgent.image_url}
          agentType={activeAgent.agent_type}
          creationType={externalCreateType || undefined}
        />
      )}
      {activeAgent?.id && (
        <NewThreadModal
          isOpen={externalCreateType !== null}
          onClose={() => setExternalCreateType(null)}
          agentId={activeAgent.id}
          agentName={agentNickname}
          agentImageUrl={activeAgent.image_url}
          agentType={activeAgent.agent_type}
          creationType={externalCreateType || undefined}
        />
      )}
    </div>
  );
};

export default TodoListPage;
